import React from 'react';

interface DataField {
  label: string;
  value: string;
}

interface SurveySuccessModalProps {
  isOpen: boolean;
  title?: string;
  dataFields: DataField[];
  actions: string[];
  onClose: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction: () => void;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  hidePrimaryButton?: boolean;
}

const SurveySuccessModal: React.FC<SurveySuccessModalProps> = ({
  isOpen,
  title = 'Success!',
  dataFields,
  actions,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
  primaryButtonText = 'Create Another',
  secondaryButtonText = 'View List',
  hidePrimaryButton = false,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[72px] right-0 bottom-0 z-40 flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-[#621C43] max-w-2xl w-full rounded-xl text-white relative shadow-xl border border-[#7a2757]">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-4 text-white hover:text-gray-300 text-xl font-bold"
          aria-label="Close popup"
        >
          &times;
        </button>

        {/* Header */}
        <div className="border-b p-4 border-[#FFF8ED]">
          <h2 className="text-lg font-semibold text-center">{title}</h2>
        </div>

        {/* Details - Dynamic Key-Value Pairs */}
        <div className="text-center mb-6 leading-relaxed text-lg px-6 pt-4">
          {dataFields.map((field, index) => (
            <p key={index} className="mb-2">
              <span className="font-medium">{field.label}:</span>{' '}
              <span className="font-semibold">{field.value}</span>
            </p>
          ))}
        </div>

        {/* Post-Creation Actions */}
        <div className="px-6 mb-8">
          <p className="text-sm mb-3 font-medium">Post-Creation Actions:</p>
          <div className="rounded-lg bg-[#A15C86]/30 p-4">
            <div className="grid grid-cols-2 gap-3 text-sm text-[#EEE] font-medium">
              {actions.map((action, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-green-400 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-between gap-4 py-6 px-6 flex-col sm:flex-row border-t border-[#7a2757]">
          {/* Primary Button - Only show when allowed */}
          {!hidePrimaryButton && onPrimaryAction && (
            <button
              onClick={onPrimaryAction}
              className="bg-[#0CA96F] flex-1 hover:bg-[#0b9e68] text-white py-3 rounded-lg text-sm font-medium transition-colors"
            >
              {primaryButtonText}
            </button>
          )}
          <button
            onClick={onSecondaryAction}
            className="flex-1 bg-[#4C86E7] hover:bg-[#4679d1] rounded-lg py-3 text-sm font-medium transition-colors"
          >
            {secondaryButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveySuccessModal;
